import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ShoppingBag, Check } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { isSaleActive, effectivePriceGbp, discountPercent } from "@/lib/sale";
import { api } from "@/lib/api";
import { trackViewItem, trackAddToCart } from "@/lib/analytics";

export default function ProductDetailsModal({ product, onClose }) {
  const { formatPrice, currency, getGbpNumber } = useCurrency();
  const { addItem } = useCart();
  const [idx, setIdx] = useState(0);
  const [added, setAdded] = useState(false);
  // Lazy-loaded gallery — fetched on open so the storefront list stays lean.
  const [fullProduct, setFullProduct] = useState(product);

  useEffect(() => {
    setFullProduct(product);
    if (!product?.id) return;
    // Fetch the full detail (with gallery_images) once the modal opens.
    let cancelled = false;
    api.get(`/collections/${product.id}`)
      .then((r) => { if (!cancelled) setFullProduct((p) => ({ ...p, ...r.data })); })
      .catch(() => { /* keep the light product if detail fetch fails */ });
    return () => { cancelled = true; };
  }, [product?.id]);

  const images = fullProduct
    ? [fullProduct.image, ...((fullProduct.gallery_images || []))].filter(Boolean)
    : [];
  const hasImages = images.length > 0;

  // Reset gallery position whenever a new product is opened
  useEffect(() => {
    setIdx(0);
    setAdded(false);
    if (product) trackViewItem(product);
  }, [product?.id]);

  // ESC to close, arrow keys to navigate gallery
  useEffect(() => {
    if (!product) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft" && hasImages) setIdx((i) => (i - 1 + images.length) % images.length);
      if (e.key === "ArrowRight" && hasImages) setIdx((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [product, hasImages, images.length, onClose]);

  if (!product) return null;
  // Once fullProduct is populated we use it for gallery/description; falls back to `product`
  const p = fullProduct || product;

  const handleAdd = () => {
    const priceString = effectivePriceGbp(p);
    const amount = getGbpNumber(priceString);
    addItem({
      id: p.id,
      name: p.name,
      category: p.category || "Luxury Wigs",
      price_gbp_string: priceString,
      unit_amount_gbp: amount || 0,
      image: p.image,
      quantity: 1,
    });
    trackAddToCart(p, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center p-2 sm:p-6"
      role="dialog"
      aria-label={`${p.name} details`}
      data-testid="product-details-modal"
    >
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
      />
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-[#0A0A0A] border border-[#D4AF37]/30 overflow-hidden flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-10 h-10 bg-black/70 hover:bg-black border border-[#D4AF37]/40 text-white/80 hover:text-[#D4AF37] flex items-center justify-center"
          aria-label="Close details"
          data-testid="product-details-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid lg:grid-cols-2 gap-0 flex-1 min-h-0">
          {/* Gallery */}
          <div className="relative bg-[#0F0F0F] border-b lg:border-b-0 lg:border-r border-[#1F1F1F] aspect-[4/5] lg:aspect-auto lg:max-h-[92vh]">
            {hasImages ? (
              <>
                <img
                  src={images[idx]}
                  alt={`${p.name} photo ${idx + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {images.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/70 hover:bg-black border border-[#D4AF37]/40 text-white hover:text-[#D4AF37] flex items-center justify-center"
                      aria-label="Previous photo"
                      data-testid="product-details-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIdx((i) => (i + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/70 hover:bg-black border border-[#D4AF37]/40 text-white hover:text-[#D4AF37] flex items-center justify-center"
                      aria-label="Next photo"
                      data-testid="product-details-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setIdx(i)}
                          className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#D4AF37]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                          aria-label={`Go to photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40 p-8 text-center">
                <div className="font-serif text-2xl text-[#D4AF37]">{p.name}</div>
                <div className="text-[0.65rem] uppercase tracking-[0.28em] mt-3">Photo coming soon</div>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col overflow-y-auto max-h-[92vh]">
            <div className="p-6 sm:p-8 flex-1">
              {p.category && (
                <div className="text-[0.65rem] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">
                  {p.category}
                </div>
              )}
              <h2 className="font-serif text-2xl sm:text-3xl text-white leading-tight" data-testid="product-details-name">
                {p.name}
              </h2>

              <div className="mt-5">
                {isSaleActive(product) ? (
                  <>
                    <div className="flex items-baseline gap-3">
                      <div className="font-serif text-3xl text-[#D4AF37]" data-testid="product-details-price">
                        {formatPrice(p.sale_price_gbp)}
                      </div>
                      <div className="text-white/75 text-xl font-bold line-through" data-testid="product-details-price-original">
                        {formatPrice(p.price_gbp)}
                      </div>
                      <div className="bg-[#25D366] text-white text-[0.65rem] uppercase tracking-[0.18em] px-2 py-0.5 font-bold">
                        {discountPercent(product)}% OFF
                      </div>
                    </div>
                    <div className="text-white/45 text-[0.65rem] uppercase tracking-[0.18em] mt-1">
                      Shown in {currency}
                    </div>
                  </>
                ) : (
                  <div className="flex items-baseline gap-3">
                    <div className="font-serif text-3xl text-[#D4AF37]" data-testid="product-details-price">
                      {formatPrice(p.price_gbp)}
                    </div>
                    <div className="text-white/45 text-[0.65rem] uppercase tracking-[0.18em]">
                      Shown in {currency}
                    </div>
                  </div>
                )}
              </div>

              <div className="gold-hairline my-6" />

              <div className="text-[0.65rem] uppercase tracking-[0.28em] text-white/55 mb-3">
                Description
              </div>
              <p className="text-white/80 text-base leading-relaxed whitespace-pre-wrap" data-testid="product-details-description">
                {p.description || "Full description coming soon."}
              </p>

              {(p.category === "Luxury Wigs") && (() => {
                const specs = [
                  ["Wig Collection", p.wig_collection],
                  ["Construction", p.construction],
                  ["Hair Type", p.hair_type],
                  ["Hair Origin", p.hair_origin === "Other" ? (p.hair_origin_custom || "Other") : p.hair_origin],
                  ["Texture", p.texture],
                  ["Length", p.length],
                  ["Lace Type", p.lace_type],
                  ["Lace Size", p.lace_size],
                  ["Density", p.density],
                ].filter(([, v]) => v && String(v).trim());
                if (specs.length === 0 && !p.care_guide) return null;
                return (
                  <>
                    {specs.length > 0 && (
                      <>
                        <div className="gold-hairline my-6" />
                        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-white/55 mb-3">Specifications</div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {specs.map(([k, v]) => (
                            <div key={k} className="flex flex-col">
                              <dt className="text-[0.65rem] uppercase tracking-[0.18em] text-white/45">{k}</dt>
                              <dd className="text-white">{v}</dd>
                            </div>
                          ))}
                        </dl>
                      </>
                    )}
                    {p.care_guide && (
                      <>
                        <div className="gold-hairline my-6" />
                        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-white/55 mb-3">Care Guide</div>
                        <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{p.care_guide}</p>
                      </>
                    )}
                  </>
                );
              })()}

              {images.length > 1 && (
                <>
                  <div className="gold-hairline my-6" />
                  <div className="text-[0.65rem] uppercase tracking-[0.28em] text-white/55 mb-3">
                    Photos ({images.length})
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {images.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setIdx(i)}
                        className={`relative aspect-square border overflow-hidden transition-all ${i === idx ? "border-[#D4AF37]" : "border-[#2A2A2A] hover:border-[#D4AF37]/50"}`}
                      >
                        <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="border-t border-[#1F1F1F] p-5 sm:p-6 space-y-3 bg-[#0A0A0A]">
              <button
                type="button"
                onClick={handleAdd}
                disabled={p.stock_status === "Out of Stock"}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-all ${
                  p.stock_status === "Out of Stock"
                    ? "bg-[#1A1A1A] text-white/40 border border-[#2A2A2A] cursor-not-allowed"
                    : added
                      ? "bg-[#25D366] text-white border border-[#25D366]"
                      : "bg-[#D4AF37] hover:bg-[#F3E5AB] text-black border border-[#D4AF37]"
                }`}
                data-testid="product-details-add"
              >
                {p.stock_status === "Out of Stock" ? (
                  <>Out of Stock — not available</>
                ) : added ? (
                  <>
                    <Check className="w-4 h-4" /> Added to bag
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" /> Add to bag — {formatPrice(effectivePriceGbp(product))}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
